import { AlertCircle, CalendarClock, CreditCard, Loader2 } from "lucide-react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { billingApi } from "@/lib/billing-client";
import { EmptyState } from "@/lib/components/empty-state";
import { ErrorAlert } from "@/lib/components/error-alert";
import { BaseNavigationBar, SettingsBackButton } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { useSalonStore } from "@/lib/store/salon";
import { SettingsSearchTrigger } from "../../search/components/settings-search-trigger";

const SUBSCRIPTION_ACCESS_ROLES = new Set(["OWNER", "FRANCHISE_ADMIN", "ADMIN", "SUPER_ADMIN"]);

type SubscriptionState = "PENDING" | "ACTIVE" | "CANCELLING" | "BLOCKED" | "CANCELLED";

const STATE_BADGE_VARIANT: Record<
  SubscriptionState,
  "default" | "secondary" | "destructive" | "outline"
> = {
  ACTIVE: "default",
  PENDING: "secondary",
  CANCELLING: "secondary",
  BLOCKED: "outline",
  CANCELLED: "destructive",
};

const STATE_LABEL: Record<SubscriptionState, string> = {
  ACTIVE: "Active",
  PENDING: "Pending",
  CANCELLING: "Cancelling",
  BLOCKED: "Blocked",
  CANCELLED: "Cancelled",
};

const PHASE_LABEL: Record<string, string> = {
  TRIAL: "Trial",
  EVERGREEN: "Active",
  DISCOUNT: "Discount",
  FIXEDTERM: "Fixed term",
};

const BILLING_PERIOD_LABEL: Record<string, string> = {
  MONTHLY: "Monthly",
  QUARTERLY: "Quarterly",
  ANNUAL: "Annual",
  BIANNUAL: "Bi-annual",
  WEEKLY: "Weekly",
  DAILY: "Daily",
};

const SCHEDULED_EVENT_LABEL: Record<string, string> = {
  CHANGE_PLAN: "Plan change",
  CHANGE: "Plan change",
  STOP_BILLING: "Billing stops",
  STOP_ENTITLEMENT: "Access ends",
  START_BILLING: "Billing starts",
  PHASE: "Phase change",
};

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toLocaleString(DateTime.DATE_MED) : "—";
}

export function SubscriptionsSettingsPage() {
  const { t } = useTranslation("settings");
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const currentFranchise = useSalonStore((state) => state.getCurrentFranchise());
  const franchiseId = currentFranchise?.franchiseId ?? "";

  const canAccessSubscription = currentSalon?.role
    ? SUBSCRIPTION_ACCESS_ROLES.has(currentSalon.role as string)
    : false;

  const subscriptionQuery = billingApi.subscriptions.getSubscription.useQuery({
    queryKey: ["dashboard-subscription", franchiseId],
    queryData: { params: { franchiseId } },
    enabled: Boolean(franchiseId) && canAccessSubscription,
  });

  const errorMessage =
    subscriptionQuery.error instanceof Error
      ? subscriptionQuery.error.message
      : subscriptionQuery.error
        ? "Failed to load subscription"
        : null;

  const bundle =
    subscriptionQuery.data && subscriptionQuery.data.status === 200
      ? subscriptionQuery.data.body
      : null;

  const subscriptionLines = bundle?.subscriptions ?? [];
  const baseSub = subscriptionLines.find((s) => s.productCategory === "BASE") ?? null;
  const addOns = subscriptionLines.filter((s) => s.productCategory === "ADD_ON");

  return (
    <>
      <BaseNavigationBar
        title={t("sections.subscriptions.title")}
        subtitle={t("sections.subscriptions.description")}
        leadElement={<SettingsBackButton />}
        trailingElements={[<SettingsSearchTrigger key="search-trigger" />]}
      />

      <PageContent>
        <div className="space-y-6">
          {!currentSalon && (
            <EmptyState
              icon={CreditCard}
              title={t("subscriptions.noSalon.title")}
              description={t("subscriptions.noSalon.description")}
            />
          )}

          {currentSalon && !canAccessSubscription && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("subscriptions.ownerOnly.title")}</AlertTitle>
              <AlertDescription>{t("subscriptions.ownerOnly.description")}</AlertDescription>
            </Alert>
          )}

          {currentSalon && canAccessSubscription && errorMessage && (
            <ErrorAlert error={errorMessage} />
          )}

          {currentSalon && canAccessSubscription && subscriptionQuery.isLoading && (
            <Card>
              <CardContent className="py-8">
                <div className="flex items-center justify-center gap-3 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>{t("common.loading")}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {currentSalon &&
            canAccessSubscription &&
            !subscriptionQuery.isLoading &&
            !errorMessage &&
            !baseSub && (
              <EmptyState
                icon={CreditCard}
                title={t("subscriptions.noSubscriptions.title")}
                description={t("subscriptions.noSubscriptions.description")}
              />
            )}

          {currentSalon && canAccessSubscription && baseSub && (
            <SubscriptionDetails baseSub={baseSub} addOns={addOns} />
          )}
        </div>
      </PageContent>
    </>
  );
}

interface SubscriptionLine {
  subscriptionId: string;
  planName: string;
  planDisplayName: string;
  productCategory: "BASE" | "ADD_ON" | "STANDALONE";
  phaseType: "TRIAL" | "EVERGREEN" | "DISCOUNT" | "FIXEDTERM";
  state: SubscriptionState;
  billingPeriod: string;
  startDate: string | null;
  chargedThroughDate: string | null;
  cancelledDate: string | null;
  billingEndDate: string | null;
  scheduledEvents: Array<{
    eventId: string;
    eventType: string;
    effectiveDate: string;
    plan: string | null;
    phase: string | null;
  }>;
}

function SubscriptionDetails({
  baseSub,
  addOns,
}: {
  baseSub: SubscriptionLine;
  addOns: SubscriptionLine[];
}) {
  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle>{baseSub.planDisplayName || baseSub.planName}</CardTitle>
            <Badge variant={STATE_BADGE_VARIANT[baseSub.state]}>{STATE_LABEL[baseSub.state]}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <DetailGrid line={baseSub} />
          {baseSub.scheduledEvents.length > 0 && (
            <>
              <Separator />
              <ScheduledEvents events={baseSub.scheduledEvents} />
            </>
          )}
        </CardContent>
      </Card>

      {addOns.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Add-ons</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {addOns.map((addon, index) => (
              <div key={addon.subscriptionId} className="space-y-3">
                {index > 0 && <Separator />}
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium">{addon.planDisplayName || addon.planName}</div>
                  <Badge variant={STATE_BADGE_VARIANT[addon.state]}>
                    {STATE_LABEL[addon.state]}
                  </Badge>
                </div>
                <DetailGrid line={addon} />
                {addon.scheduledEvents.length > 0 && (
                  <ScheduledEvents events={addon.scheduledEvents} />
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </>
  );
}

function DetailGrid({ line }: { line: SubscriptionLine }) {
  const phase = PHASE_LABEL[line.phaseType] ?? line.phaseType;
  const billing = BILLING_PERIOD_LABEL[line.billingPeriod] ?? line.billingPeriod;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
      <DetailRow label="Phase" value={phase} />
      <DetailRow label="Billing" value={billing} />
      <DetailRow label="Started" value={formatDate(line.startDate)} />
      <DetailRow
        label={line.cancelledDate ? "Period ends" : "Renews on"}
        value={formatDate(line.chargedThroughDate)}
      />
      {line.cancelledDate && (
        <DetailRow label="Cancelled on" value={formatDate(line.cancelledDate)} />
      )}
      {line.billingEndDate && (
        <DetailRow label="Billing ends" value={formatDate(line.billingEndDate)} />
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 sm:flex-col sm:items-start sm:justify-start">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}

function ScheduledEvents({ events }: { events: SubscriptionLine["scheduledEvents"] }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium">
        <CalendarClock className="h-4 w-4 text-muted-foreground" />
        Upcoming changes
      </div>
      <ul className="space-y-1 text-sm text-muted-foreground">
        {events.map((event) => {
          const label = SCHEDULED_EVENT_LABEL[event.eventType] ?? event.eventType;
          const detail = event.plan ? ` → ${event.plan}` : "";
          return (
            <li key={event.eventId} className="flex items-center justify-between gap-3">
              <span>
                {label}
                {detail}
              </span>
              <span className="tabular-nums">{formatDate(event.effectiveDate)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
